import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Crown } from "lucide-react";
import styles from "./PaymentSuccess.module.css";

const PaymentSuccess = () => {
  const [status, setStatus] = useState("processing"); // "processing", "success", "error"
  const [message, setMessage] = useState("Verifying your payment...");
  const [countdown, setCountdown] = useState(5);
  const [diagnosticInfo, setDiagnosticInfo] = useState(null);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to start the countdown for redirect
  const startRedirectCountdown = useCallback(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/dashboard");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [navigate]);

  // Helper function to check if a subscription is valid based on end timestamp
  const hasValidSubscription = (userData) => {
    if (userData.subscriptionEndTimestamp) {
      if (userData.subscriptionEndTimestamp.toDate) {
        const endDate = userData.subscriptionEndTimestamp.toDate();
        if (endDate > new Date()) {
          console.log("Valid subscription found via subscriptionEndTimestamp - expires:", endDate);
          return true;
        }
      } else if (
        typeof userData.subscriptionEndTimestamp === "string" ||
        userData.subscriptionEndTimestamp instanceof Date
      ) {
        const endDate = new Date(userData.subscriptionEndTimestamp);
        if (!isNaN(endDate) && endDate > new Date()) {
          console.log("Valid subscription found via subscriptionEndTimestamp string - expires:", endDate);
          return true;
        }
      }
    }

    // Also check for proEndDate
    if (userData.proEndDate) {
      if (userData.proEndDate.toDate) {
        const endDate = userData.proEndDate.toDate();
        if (endDate > new Date()) {
          console.log("Valid subscription found via proEndDate - expires:", endDate);
          return true;
        }
      } else if (
        typeof userData.proEndDate === "string" ||
        userData.proEndDate instanceof Date
      ) {
        const endDate = new Date(userData.proEndDate);
        if (!isNaN(endDate) && endDate > new Date()) {
          console.log("Valid subscription found via proEndDate string - expires:", endDate);
          return true;
        }
      }
    }

    // Fallback: check for trusted backend-written paymentStatus
    if (userData.paymentStatus === "paid") {
      console.log("Payment is marked as paid, but no future subscription end date found");
      return "paid-only";
    }
    return false;
  };

  const isBackendConfirmedPro = (userData) => {
    return userData?.tierType === "pro" || !!hasValidSubscription(userData);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get("session_id");

    const diagnosticData = {
      timestamp: new Date().toISOString(),
      sessionId: sessionId,
      path: location.pathname,
      search: location.search,
    };

    console.log("Payment Success Page - Initial Load:", diagnosticData);
    setDiagnosticInfo(diagnosticData);

    const checkUserStatus = async (userId, attempt = 1) => {
      const maxChecks = 6;

      if (!userId) {
        setStatus("error");
        setMessage("No user info found. Please log in and check your account status.");
        return;
      }

      console.log(`Check ${attempt}/${maxChecks} for backend-confirmed Pro status...`);

      try {
        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();

          if (isBackendConfirmedPro(userData)) {
            setStatus("success");
            setMessage("Your account has been upgraded to Pro! Redirecting to your dashboard...");
            localStorage.removeItem("upgradeUserId");
            startRedirectCountdown();
            return;
          }
        }

        if (attempt >= maxChecks) {
          console.log("Payment not confirmed by backend after polling.");
          setStatus("processing");
          setMessage("Your payment is still being confirmed. Please check your dashboard shortly.");
          setVerificationAttempted(true);
          return;
        }

        setStatus("processing");
        setMessage("Finalizing your upgrade. This may take a moment...");
        setTimeout(() => checkUserStatus(userId, attempt + 1), 5000);
      } catch (error) {
        console.error("Error checking user status:", error);
        setStatus("error");
        setMessage("Error verifying account status. Please check your dashboard or contact support.");
      }
    };

    const verifyPaymentWithSession = async (firebaseUser) => {
      diagnosticData.userAuthenticated = !!firebaseUser;
      diagnosticData.userId = firebaseUser?.uid;
      setDiagnosticInfo(diagnosticData);
      setVerificationAttempted(true);

      try {
        const functions = getFunctions();
        const verifyPayment = httpsCallable(functions, "verifyPayment");
        const result = await verifyPayment({ sessionId });

        diagnosticData.verificationResult = result.data;
        setDiagnosticInfo(diagnosticData);
        console.log("Payment verification result:", result.data);

        setStatus("processing");
        setMessage("Payment verified. Waiting for your account to update...");
        setTimeout(() => checkUserStatus(firebaseUser?.uid), 3000);
      } catch (error) {
        console.error("Error verifying payment:", error);
        diagnosticData.error = error.message;
        setDiagnosticInfo(diagnosticData);

        setStatus("processing");
        setMessage("Finalizing your upgrade. This may take a moment...");
        setTimeout(() => checkUserStatus(firebaseUser?.uid), 5000);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const upgradeUserId = localStorage.getItem("upgradeUserId");
      const userId = user?.uid || upgradeUserId;

      if (!userId) {
        setStatus("error");
        setMessage("No user info found. Please log in and check your account status.");
        return;
      }

      if (sessionId) {
        console.log(`Starting payment verification with session ID: ${sessionId}`);
        verifyPaymentWithSession(user || { uid: userId });
      } else {
        console.log("No session ID found in URL. Checking backend-confirmed account status...");
        setVerificationAttempted(true);
        setStatus("processing");
        setMessage("Processing your payment. This may take a moment...");
        checkUserStatus(userId);
      }
    });

    return () => unsubscribe();
  }, [location.search, location.pathname, startRedirectCountdown]);

  const handleLoginClick = () => navigate("/");
  const handleDashboardClick = () => navigate("/dashboard");

  // Show diagnostic info in development only
  const showDiagnostics = process.env.NODE_ENV === "development";

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {status === "processing" && (
          <div className={styles.processing}>
            <div className={styles.spinner}></div>
            <h2>Processing Your Upgrade</h2>
            <p>{message}</p>
            {/* Add a manual dashboard button after 5 seconds of processing */}
            {verificationAttempted && (
              <button
                className={styles.dashboardButton}
                onClick={handleDashboardClick}
              >
                Go to Dashboard
              </button>
            )}
          </div>
        )}
        {status === "success" && (
          <div className={styles.success}>
            <div className={styles.iconWrapper}>
              <Crown size={48} className={styles.crownIcon} />
            </div>
            <h2>Welcome to Pro!</h2>
            <p>{message}</p>
            {auth.currentUser ? (
              <div className={styles.redirectMessage}>
                <p>Redirecting in {countdown} seconds...</p>
                <button
                  className={styles.dashboardButton}
                  onClick={handleDashboardClick}
                >
                  Go to Dashboard Now
                </button>
              </div>
            ) : (
              <button
                className={styles.loginButton}
                onClick={handleLoginClick}
              >
                Log In Now
              </button>
            )}
          </div>
        )}
        {status === "error" && (
          <div className={styles.error}>
            <h2>Something went wrong</h2>
            <p>{message}</p>
            <div className={styles.buttons}>
              <button
                className={styles.dashboardButton}
                onClick={handleDashboardClick}
              >
                Go to Dashboard
              </button>
              <button className={styles.loginButton} onClick={handleLoginClick}>
                Go to Login
              </button>
            </div>
          </div>
        )}

        {showDiagnostics && diagnosticInfo && (
          <div className={styles.diagnostics}>
            <h3>Diagnostic Info</h3>
            <pre>{JSON.stringify(diagnosticInfo, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
